import { useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TablePagination from '@mui/material/TablePagination';
import PanelContent from '@shared/components/PanelContent';
import PanelSkeleton from '@shared/components/PanelSkeleton';
import RetryableError from '@shared/components/RetryableError';
import CollapsibleFilterBar from '@shared/components/CollapsibleFilterBar';
import CoverageFilter from '@shared/components/CoverageFilter';
import { ApiRequestError } from '@api/client/apiClient';
import { productTypeChipSx } from '@shared/constants/product';
import { useConnectivityStore } from '@shared/hooks/useConnectivityStore';
import { useMapStore } from '@shared/hooks/useMapStore';
import { useDebouncedValue } from '@shared/hooks/useDebouncedValue';
import { useBasket } from '@features/basket/hooks/useBasket';
import { useHoldings } from './hooks/useHoldings';
import { useAvcsCells } from './hooks/useAvcsCells';
import HoldingsList from './components/HoldingsList';
import AvcsCellList from './components/AvcsCellList';
import PermitImportDialog from './components/PermitImportDialog';
import { HOLDINGS_PAGE_SIZE } from './constants';

const TYPE_OPTIONS: Array<{ label: string; value: string }> = [
  { label: 'All', value: '' },
  { label: 'AVCS', value: 'AVCS' },
  { label: 'ADP', value: 'ADP' },
  { label: 'e-NP', value: 'e-NP' },
];
const STATUS_OPTIONS = ['active', 'expiring', 'expired'] as const;
type BasketFilter = 'all' | 'in' | 'not-in';
const SEARCH_DEBOUNCE_MS = 300;

export default function HoldingsPage() {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [coverageFilter, setCoverageFilter] = useState<string[]>([]);
  const [basketFilter, setBasketFilter] = useState<BasketFilter>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(HOLDINGS_PAGE_SIZE);
  const [cellsPage, setCellsPage] = useState(0);
  const [cellsRowsPerPage, setCellsRowsPerPage] = useState(100);
  const [permitDialogOpen, setPermitDialogOpen] = useState(false);

  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);

  const isOffline = useConnectivityStore((s) => s.state) !== 'online';
  const avcsMode = useMapStore((s) => s.avcsMode);
  const selectedProductId = useMapStore((s) => s.selectedProductId);
  const setSelectedProductId = useMapStore((s) => s.setSelectedProductId);
  const setFitProductId = useMapStore((s) => s.setFitProductId);
  const setHoldingsProducts = useMapStore((s) => s.setHoldingsProducts);
  const setActiveProductsRouteId = useMapStore((s) => s.setActiveProductsRouteId);
  const setCatalogProducts = useMapStore((s) => s.setCatalogProducts);
  const setBasketProductsActive = useMapStore((s) => s.setBasketProductsActive);
  const basketItems = useBasket((s) => s.items);

  // Map integration: clear conflicting sources on mount
  useEffect(() => {
    setActiveProductsRouteId(null);
    setCatalogProducts(null);
    setBasketProductsActive(false);
    return () => setHoldingsProducts(null);
  }, [setActiveProductsRouteId, setCatalogProducts, setBasketProductsActive, setHoldingsProducts]);

  const { data, isLoading, error, refetch } = useHoldings({
    search: debouncedSearch || undefined,
    type: typeFilter || undefined,
    status: statusFilter.length === 1 ? statusFilter[0] : undefined,
    coverage: coverageFilter.length === 1 ? coverageFilter[0] : undefined,
    page,
    size: rowsPerPage,
  });

  const { data: avcsCellsData, isLoading: cellsLoading } = useAvcsCells({
    page: cellsPage,
    size: cellsRowsPerPage,
  });

  // Sync holdings to the map layer
  useEffect(() => {
    setHoldingsProducts(data?.items ?? null);
  }, [data?.items, setHoldingsProducts]);

  // Apply client-side multi-value filters that the server doesn't support as arrays
  let holdings = data?.items ?? [];
  if (statusFilter.length > 1) {
    holdings = holdings.filter((h) => statusFilter.includes(h.status));
  }
  if (coverageFilter.length > 1) {
    holdings = holdings.filter((h) => h.coverage && coverageFilter.includes(h.coverage));
  }
  if (basketFilter !== 'all') {
    const basketIds = new Set(basketItems.map((i) => i.productId));
    holdings = holdings.filter((h) =>
      basketFilter === 'in' ? basketIds.has(h.productId) : !basketIds.has(h.productId),
    );
  }

  // Determine if we should show AVCS cells mode
  const showCells = avcsMode === 'cells' && (typeFilter === '' || typeFilter === 'AVCS');

  function handleStatusChange(event: SelectChangeEvent<string[]>) {
    const v = event.target.value;
    setStatusFilter(typeof v === 'string' ? v.split(',') : v);
    setPage(0);
  }

  function handleCoverageChange(values: string[]) {
    setCoverageFilter(values);
    setPage(0);
  }

  const activeFilterCount =
    (typeFilter ? 1 : 0) +
    (statusFilter.length > 0 ? 1 : 0) +
    (coverageFilter.length > 0 ? 1 : 0) +
    (basketFilter !== 'all' ? 1 : 0);

  function handleClearFilters() {
    setTypeFilter('');
    setStatusFilter([]);
    setCoverageFilter([]);
    setBasketFilter('all');
    setPage(0);
  }

  function handleSearchChange(value: string) {
    setSearch(value);
    setPage(0);
  }

  function handlePageChange(_: unknown, newPage: number) {
    setPage(newPage);
  }

  function handleRowsPerPageChange(event: React.ChangeEvent<HTMLInputElement>) {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  }

  if (!data && isLoading) return <PanelSkeleton />;

  if (error && !data) {
    const correlationId =
      error instanceof ApiRequestError ? error.apiError.correlationId : undefined;
    return (
      <RetryableError
        message={error?.message ?? 'Failed to load holdings'}
        correlationId={correlationId}
        onRetry={() => void refetch()}
      />
    );
  }

  return (
    <PanelContent>
      <div data-testid="page-holdings" className="flex flex-col gap-2">
        {/* Header row */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <TextField
            size="small"
            placeholder="Search holdings..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ flex: 1, mr: 1 }}
          />
          <Button
            size="small"
            startIcon={<UploadFileIcon />}
            onClick={() => setPermitDialogOpen(true)}
            disabled={isOffline}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            Import Permit
          </Button>
        </Box>

        <CollapsibleFilterBar activeFilterCount={activeFilterCount} onClear={handleClearFilters}>
          {/* Product type chips */}
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {TYPE_OPTIONS.map((opt) => (
              <Chip
                key={opt.value}
                label={opt.label}
                size="small"
                variant={typeFilter === opt.value ? 'filled' : 'outlined'}
                color={opt.value ? undefined : typeFilter === opt.value ? 'primary' : 'default'}
                sx={productTypeChipSx(opt.value, typeFilter === opt.value)}
                onClick={() => {
                  setTypeFilter(opt.value);
                  setPage(0);
                }}
              />
            ))}
          </Box>

          {/* Filter row */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                multiple
                value={statusFilter}
                onChange={handleStatusChange}
                label="Status"
                renderValue={(selected) => selected.join(', ')}
              >
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ minWidth: 130 }}>
              <CoverageFilter value={coverageFilter} onChange={handleCoverageChange} />
            </Box>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Basket</InputLabel>
              <Select
                value={basketFilter}
                onChange={(e) => {
                  setBasketFilter(e.target.value as BasketFilter);
                  setPage(0);
                }}
                label="Basket"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="in">In Basket</MenuItem>
                <MenuItem value="not-in">Not in Basket</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CollapsibleFilterBar>

        {/* Content */}
        {showCells ? (
          <AvcsCellList cells={avcsCellsData?.items ?? []} loading={cellsLoading} />
        ) : (
          <HoldingsList
            holdings={holdings}
            loading={isLoading}
            onSelectHolding={(h) => setSelectedProductId(h.productId)}
            onDoubleClickHolding={(h) => setFitProductId(h.productId)}
            selectedProductId={selectedProductId}
          />
        )}

        {/* Pagination */}
        {!showCells && (
          <TablePagination
            component="div"
            count={data?.totalItems ?? 0}
            page={page}
            labelRowsPerPage=""
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 25, 50]}
          />
        )}
        {showCells && (
          <TablePagination
            component="div"
            count={avcsCellsData?.totalItems ?? 0}
            page={cellsPage}
            labelRowsPerPage=""
            rowsPerPage={cellsRowsPerPage}
            onPageChange={(_, p) => setCellsPage(p)}
            onRowsPerPageChange={(e) => {
              setCellsRowsPerPage(Number.parseInt(e.target.value, 10));
              setCellsPage(0);
            }}
            rowsPerPageOptions={[50, 100]}
          />
        )}

        <PermitImportDialog
          open={permitDialogOpen}
          onClose={() => setPermitDialogOpen(false)}
          disabled={isOffline}
        />
      </div>
    </PanelContent>
  );
}
